import { useEffect, useState } from 'react';
import { Box, Typography, Button, Chip, Paper, Tabs, Tab, TextField, MenuItem, Grid } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import supabase from '../../../supabaseClient'; 
import AddOrders from './AddOrder';
import OrdersDetails from './OrdersDetails';
import { formatCurrency } from './currency';

const realizacjaStyles = {
    'nowe': { bgcolor: '#E4EDFB', color: '#2E6DC4' },
    'w_realizacji': { bgcolor: '#FCEBD8', color: '#C97A22' },
    'zrealizowane': { bgcolor: '#EAEAEA', color: '#6B6B6B' },
};

const realizacjaLabels = {
    'nowe': 'Nowe',
    'w_realizacji': 'W realizacji',
    'zrealizowane': 'Zrealizowane',
};

const statusChip = (value, styles) => (
    <Chip
        label={realizacjaLabels[value] || value}
        size="small"
        sx={{
            ...(styles[value] || { bgcolor: '#EAEAEA', color: '#6B6B6B' }),
            fontWeight: 600,
            borderRadius: '8px',
            height: 26,
        }}
    />
);

function SummaryCard({ label, value, sublabel }) {
    return (
        <Box sx={{
            flex: 1, minWidth: 200,
            bgcolor: 'background.paper',
            border: '1px solid #ECE7DE',
            borderRadius: 3,
            px: 2.5, py: 2,
        }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.03em', color: 'text.secondary', mb: 1 }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main' }}>
                {value}
            </Typography>
            {sublabel && (
                <Typography sx={{ fontSize: '14px', color: 'text.secondary', mt: 0.5 }}>
                    {sublabel}
                </Typography>
            )}
        </Box>
    );
}

const stackedListCell = (getValue) => (params) => (
    <Box sx={{ py: 0.5 }}>
        {params.row.produkty.map((item) => (
            <Box key={item.id} sx={{ fontSize: '13px', lineHeight: 1.6 }}>
                {getValue(item, params.row)}
            </Box>
        ))}
    </Box>
);

// Domyślna dolna granica daty zamówienia - 60 dni wstecz
const getDefaultDateFrom = () => {
    const d = new Date();
    d.setDate(d.getDate() - 60);
    return d.toISOString().slice(0, 10);
};

// Funkcja pomocnicza do obliczania tygodnia ISO roku
const getWeekNumber = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Kolumny DataGrid ze osobną, pogrubioną kolumną dla numeru tygodnia
const columns = [
    {
        field: 'tydzien',
        headerName: 'TYDZIEŃ',
        flex: 0.6,
        minWidth: 80,
        renderCell: (params) => {
            const weekNum = getWeekNumber(params.row.planning_data);
            if (!weekNum) return <span style={{ color: '#B0AAA0' }}>—</span>;
            return <strong style={{ fontWeight: 700 }}>Tydzień {weekNum}</strong>;
        }
    },
    { field: 'kontrahent', headerName: 'KONTRAHENT', flex: 1.2, minWidth: 150, renderCell: (params) => <strong>{params.value}</strong> },
    {
        field: 'produkty',
        headerName: 'PRODUKT(Y)',
        flex: 1.5,
        minWidth: 180,
        renderCell: stackedListCell((item) => item.product),
    },
    {
        field: 'ilosc',
        headerName: 'ILOŚĆ',
        flex: 0.6,
        minWidth: 80,
        renderCell: stackedListCell((item) => `${item.amount} szt.`),
    },
    {
        field: 'cena',
        headerName: 'CENA',
        flex: 0.7,
        minWidth: 90,
        renderCell: stackedListCell((item, row) => formatCurrency(item.unit_price, row.currency)),
    },
    {
        field: 'wartosc',
        headerName: 'WARTOŚĆ',
        flex: 0.9,
        minWidth: 110,
        renderCell: (params) => formatCurrency(params.value ?? 0, params.row.currency),
    },
    {
        field: 'dostepne',
        headerName: 'DOSTĘPNE',
        flex: 0.7,
        minWidth: 90,
        renderCell: () => <span style={{ color: '#B0AAA0' }}>—</span>,
    },
    {
        field: 'stan',
        headerName: 'STAN',
        flex: 1,
        minWidth: 130,
        renderCell: () => <span style={{ color: '#B0AAA0' }}>—</span>,
    },
    {
        field: 'realizacja',
        headerName: 'REALIZACJA',
        flex: 1,
        minWidth: 130,
        renderCell: (params) => statusChip(params.value, realizacjaStyles),
    },
    { field: 'uwagi', headerName: 'UWAGI', flex: 1.4, minWidth: 160 },
];

function Orders() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOrderOpen, setAddOrderOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [activeTab, setActiveTab] = useState('aktywne');
    const [idConfiguration, setIdConfiguration] = useState(null);

    // Stan dla filtrów
    const [filters, setFilters] = useState({
        dateFrom: '',
        dateTo: '',
        planningDateFrom: '',
        planningDateTo: '',
        kontrahent: '',
        produkt: '',
        realizacja: 'wszystkie',
        uwagi: '',
    });

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        const getInitialData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('id_configuration')
                    .eq('id', session.user.id)
                    .single();

                if (profileData) {
                    setIdConfiguration(profileData.id_configuration);
                }
            }
        };
        getInitialData();
    }, []);

    async function fetchOrders() {
        setLoading(true);

        let query = supabase
            .from('orders')
            .select(`
                id,
                order_date,
                planning_order_date,
                realizacja,
                total_price,
                note,
                currency,
                kontrahenci ( name ),
                order_items ( id, product, amount, unit_price, total_price )
            `)
            .eq('id_configuration', idConfiguration)
            .order('order_date', { ascending: false });

        if (activeTab === 'zrealizowane') {
            query = query.eq('realizacja', 'zrealizowane');
            query = query.gte('order_date', filters.dateFrom || getDefaultDateFrom());
        } else {
            query = query.in('realizacja', ['nowe', 'w_realizacji']);
            if (filters.dateFrom) query = query.gte('order_date', filters.dateFrom);
        }
        if (filters.dateTo) query = query.lte('order_date', filters.dateTo);

        const { data, error } = await query;

        if (error) {
            console.error('Błąd pobierania zamówień:', error);
            setLoading(false);
            return;
        }

        const mappedRows = data.map((order) => ({
            id: order.id,
            data: order.order_date,
            planning_data: order.planning_order_date ?? null,
            planning_week: getWeekNumber(order.planning_order_date),
            kontrahent: order.kontrahenci?.name ?? '—',
            produkty: order.order_items,
            wartosc: order.total_price,
            currency: order.currency ?? 'PLN',
            realizacja: order.realizacja,
            uwagi: order.note ?? '',
        }));

        setRows(mappedRows);
        setLoading(false);
    }

    useEffect(() => {
        if (idConfiguration) fetchOrders();
    }, [idConfiguration, activeTab, filters.dateFrom, filters.dateTo]);

    const filteredRows = rows.filter((row) => {
        if (filters.dateFrom && row.data < filters.dateFrom) return false;
        if (filters.dateTo && row.data > filters.dateTo) return false;

        if (filters.planningDateFrom && (!row.planning_data || row.planning_data < filters.planningDateFrom)) return false;
        if (filters.planningDateTo && (!row.planning_data || row.planning_data > filters.planningDateTo)) return false;

        if (filters.kontrahent && !row.kontrahent.toLowerCase().includes(filters.kontrahent.toLowerCase())) return false;

        if (filters.produkt) {
            const hasProduct = row.produkty.some((item) => item.product.toLowerCase().includes(filters.produkt.toLowerCase()));
            if (!hasProduct) return false;
        }

        if (filters.realizacja !== 'wszystkie' && row.realizacja !== filters.realizacja) return false;

        if (filters.uwagi && !row.uwagi.toLowerCase().includes(filters.uwagi.toLowerCase())) return false;

        return true;
    });

    const orderCount = filteredRows.length;

    const totalItemsQty = filteredRows.reduce(
        (sum, row) => sum + row.produkty.reduce((s, item) => s + Number(item.amount || 0), 0),
        0
    );
    const avgQtyPerOrder = orderCount ? totalItemsQty / orderCount : 0;

    const byCurrency = {};
    filteredRows.forEach((row) => {
        const cur = row.currency ?? 'PLN';
        if (!byCurrency[cur]) byCurrency[cur] = { total: 0, count: 0 };
        byCurrency[cur].total += Number(row.wartosc || 0);
        byCurrency[cur].count += 1;
    });
    const currencies = Object.keys(byCurrency);

    const formatMultiCurrency = (getAmount) =>
        currencies.length
            ? currencies.map((cur) => formatCurrency(getAmount(byCurrency[cur]), cur)).join(' + ')
            : formatCurrency(0, 'PLN');

    const totalValueLabel = formatMultiCurrency((c) => c.total);
    const avgOrderValueLabel = formatMultiCurrency((c) => (c.count ? c.total / c.count : 0));

    return (
        <Box sx={{ minHeight: '100%'}}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography sx={{ fontSize: '30px', fontWeight: 700, color: 'primary.main' }}>Zamówienia</Typography>
                <Button
                    variant="contained"
                    onClick={() => setAddOrderOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5, py: 1 }}
                >
                    + Nowe zamówienie
                </Button>
            </Box>

            <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                sx={{
                    mb: 2,
                    minHeight: 'unset',
                    '& .MuiTabs-indicator': { display: 'none' },
                }}
            >
                <Tab
                    label="Aktywne"
                    value="aktywne"
                    disableRipple
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '10px',
                        minHeight: 'unset',
                        px: 2.5,
                        py: 1,
                        mr: 1,
                        color: 'text.secondary',
                        '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff' },
                    }}
                />
                <Tab
                    label="Zrealizowane"
                    value="zrealizowane"
                    disableRipple
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '10px',
                        minHeight: 'unset',
                        px: 2.5,
                        py: 1,
                        color: 'text.secondary',
                        '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff' },
                    }}
                />
            </Tabs>

            {/* Panel Filtrów */}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    border: '1px solid #ECE7DE',
                }}
            >
                <Typography sx={{ fontSize: '14px', fontWeight: 700, mb: 2, color: 'text.secondary' }}>
                    Filtry wyszukiwania
                </Typography>
                <Grid container spacing={2}>
                    {/* Data od */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            Data od
                        </Typography>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            value={filters.dateFrom}
                            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                            sx={{
                                '& input[type="date"]::-webkit-datetime-edit': {
                                    color: filters.dateFrom ? 'inherit' : 'transparent',
                                },
                                '& input[type="date"]:focus::-webkit-datetime-edit': {
                                    color: 'inherit',
                                },
                            }}
                        />
                        {filters.dateFrom ? (
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, mt: 0.5, display: 'block' }}>
                                Tydzień {getWeekNumber(filters.dateFrom)}
                            </Typography>
                        ) : (
                            activeTab === 'zrealizowane' && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                                    Domyślnie: ostatnie 60 dni
                                </Typography>
                            )
                        )}
                    </Grid>

                    {/* Data do */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            Data do
                        </Typography>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            value={filters.dateTo}
                            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                            sx={{
                                '& input[type="date"]::-webkit-datetime-edit': {
                                    color: filters.dateTo ? 'inherit' : 'transparent',
                                },
                                '& input[type="date"]:focus::-webkit-datetime-edit': {
                                    color: 'inherit',
                                },
                            }}
                        />
                        {filters.dateTo && (
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, mt: 0.5, display: 'block' }}>
                                Tydzień {getWeekNumber(filters.dateTo)}
                            </Typography>
                        )}
                    </Grid>

                    {/* Planowana data dostawy od */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            Plan. dostawa od
                        </Typography>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            value={filters.planningDateFrom}
                            onChange={(e) => handleFilterChange('planningDateFrom', e.target.value)}
                            sx={{
                                '& input[type="date"]::-webkit-datetime-edit': {
                                    color: filters.planningDateFrom ? 'inherit' : 'transparent',
                                },
                                '& input[type="date"]:focus::-webkit-datetime-edit': {
                                    color: 'inherit',
                                },
                            }}
                        />
                        {filters.planningDateFrom && (
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, mt: 0.5, display: 'block' }}>
                                Tydzień {getWeekNumber(filters.planningDateFrom)}
                            </Typography>
                        )}
                    </Grid>

                    {/* Planowana data dostawy do */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            Plan. dostawa do
                        </Typography>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            value={filters.planningDateTo}
                            onChange={(e) => handleFilterChange('planningDateTo', e.target.value)}
                            sx={{
                                '& input[type="date"]::-webkit-datetime-edit': {
                                    color: filters.planningDateTo ? 'inherit' : 'transparent',
                                },
                                '& input[type="date"]:focus::-webkit-datetime-edit': {
                                    color: 'inherit',
                                },
                            }}
                        />
                        {filters.planningDateTo && (
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, mt: 0.5, display: 'block' }}>
                                Tydzień {getWeekNumber(filters.planningDateTo)}
                            </Typography>
                        )}
                    </Grid>

                    {/* Kontrahent */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            Kontrahent
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Szukaj kontrahenta..."
                            value={filters.kontrahent}
                            onChange={(e) => handleFilterChange('kontrahent', e.target.value)}
                        />
                    </Grid>

                    {/* Produkt */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            Produkt
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Szukaj produktu..."
                            value={filters.produkt}
                            onChange={(e) => handleFilterChange('produkt', e.target.value)}
                        />
                    </Grid>

                    {/* Status */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            Status
                        </Typography>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            value={filters.realizacja}
                            onChange={(e) => handleFilterChange('realizacja', e.target.value)}
                        >
                            <MenuItem value="wszystkie">Wszystkie statusy</MenuItem>
                            <MenuItem value="nowe">Nowe</MenuItem>
                            <MenuItem value="w_realizacji">W realizacji</MenuItem>
                            <MenuItem value="zrealizowane">Zrealizowane</MenuItem>
                        </TextField>
                    </Grid>

                    {/* Uwagi */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            Uwagi
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Szukaj w uwagach..."
                            value={filters.uwagi}
                            onChange={(e) => handleFilterChange('uwagi', e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <SummaryCard
                    label="ŚREDNIA WARTOŚĆ ZAMÓWIENIA"
                    value={avgOrderValueLabel}
                    sublabel={`z ${orderCount} zamówień`}
                />
                <SummaryCard
                    label="ŚREDNIA ILOŚĆ W ZAMÓWIENIU"
                    value={`${avgQtyPerOrder.toFixed(0)} szt.`}
                    sublabel="łącznie w okresie"
                />
                <SummaryCard
                    label="WARTOŚĆ ZAMÓWIEŃ W OKRESIE"
                    value={totalValueLabel}
                    sublabel={filters.dateFrom || filters.dateTo ? `${filters.dateFrom || '…'} → ${filters.dateTo || '…'}` : 'wszystkie daty'}
                />
                <SummaryCard
                    label="ILOŚĆ ZAMÓWIEŃ W OKRESIE"
                    value={orderCount}
                    sublabel={`${totalItemsQty} szt. łącznie`}
                />
            </Box>

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    border: '1px solid #ECE7DE',
                }}
            >
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    onRowClick={(params) => setSelectedOrderId(params.id)}
                    loading={loading}
                    getRowHeight={() => 'auto'}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    hideFooter={filteredRows.length <= 100}
                    columnHeaderHeight={44}
                    sx={{
                        cursor: 'pointer', 
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#FAF7F2',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: 'text.secondary',
                            fontWeight: 700,
                            fontSize: '12px',
                            letterSpacing: '0.03em',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #F1EDE6',
                            py: 1,
                        },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#FEFCF9',
                        },
                    }}
                />
            </Paper>

            <AddOrders
                open={addOrderOpen}
                onClose={() => setAddOrderOpen(false)}
                onOrderCreated={fetchOrders}
            />

            <OrdersDetails
                open={Boolean(selectedOrderId)}
                orderId={selectedOrderId}
                onClose={() => setSelectedOrderId(null)}
                onStatusChanged={fetchOrders}
            />
        </Box>
    );
}

export default Orders;